import { getAiInferencePresignedUrl } from '../../api/aiInference';
import { getRoomObjects } from '../../api/rooms';
import { pickRoomObjectImageUrl, resolveRoomObjectImageSrcForDisplay } from '../roomObjectImage';

/** Resolve a displayable thumbnail URL for a room object (fresh presign when needed). */
export async function resolveRoomObjectThumbSrcAsync(obj) {
  const raw = pickRoomObjectImageUrl(obj);
  if (!raw || !String(raw).trim()) return '';
  const trimmed = String(raw).trim();
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('//')) {
    return resolveRoomObjectImageSrcForDisplay(trimmed);
  }
  try {
    const presigned = await getAiInferencePresignedUrl(trimmed);
    const url = presigned?.url ?? presigned?.presignedUrl;
    if (url) return resolveRoomObjectImageSrcForDisplay(String(url).trim());
  } catch (err) {
    console.warn('Presign failed for room object image key:', trimmed, err);
  }
  return '';
}

/** Map room object id → { thumbSrc, name, category } for the given ids (or all when ids omitted). */
export async function fetchRoomObjectThumbMap(roomId, objectIds = null) {
  if (roomId == null) return new Map();
  const objects = await getRoomObjects(roomId);
  const idSet =
    objectIds != null && objectIds.length > 0
      ? new Set(objectIds.map((id) => String(id)))
      : null;
  const targets = idSet
    ? objects.filter((o) => o?.id != null && idSet.has(String(o.id)))
    : objects;
  const entries = await Promise.all(
    targets.map(async (o) => {
      const thumbSrc = await resolveRoomObjectThumbSrcAsync(o);
      return [
        String(o.id),
        {
          thumbSrc,
          name: o.name,
          category: o.category,
        },
      ];
    }),
  );
  const map = new Map();
  for (const [id, meta] of entries) {
    map.set(id, meta);
  }
  return map;
}

/** Resolve a loadable image URL for a canvas placement (presign S3 keys / refresh from room object). */
export async function resolvePlacementImageSrc(placement, roomId) {
  const raw = typeof placement?.thumbSrc === 'string' ? placement.thumbSrc.trim() : '';
  const isHttp = raw && (/^https?:\/\//i.test(raw) || raw.startsWith('//'));

  if (isHttp) {
    return resolveRoomObjectImageSrcForDisplay(raw);
  }

  if (roomId != null && placement?.roomObjectId != null) {
    try {
      const objects = await getRoomObjects(roomId);
      const found = objects.find(
        (o) => Number(o.id) === Number(placement.roomObjectId),
      );
      if (found) {
        const fromObject = await resolveRoomObjectThumbSrcAsync(found);
        if (fromObject) return fromObject;
      }
    } catch (err) {
      console.warn('Failed to resolve placement thumb from room object:', err);
    }
  }

  if (raw) {
    try {
      const presigned = await getAiInferencePresignedUrl(raw);
      const url = presigned?.url ?? presigned?.presignedUrl;
      if (url) return resolveRoomObjectImageSrcForDisplay(String(url).trim());
    } catch (err) {
      console.warn('Presign failed for placement thumb key:', raw, err);
    }
  }

  return '';
}

export function applyThumbMapToCuratedItems(items, thumbMap) {
  if (!Array.isArray(items) || !(thumbMap instanceof Map)) return items;
  return items.map((item) => {
    const meta = thumbMap.get(String(item.id));
    if (!meta) {
      return { ...item, thumbSrc: item.thumbSrc ?? '' };
    }
    return {
      ...item,
      thumbSrc: meta.thumbSrc || item.thumbSrc || '',
      name: meta.name ?? item.name,
      category: meta.category ?? item.category,
    };
  });
}

export function applyThumbMapToPlacedModels(placements, thumbMap) {
  if (!Array.isArray(placements) || !(thumbMap instanceof Map)) return placements;
  return placements.map((p) => {
    const meta = thumbMap.get(String(p.roomObjectId));
    if (!meta) {
      return { ...p, thumbSrc: p.thumbSrc ?? '' };
    }
    return {
      ...p,
      thumbSrc: meta.thumbSrc || p.thumbSrc || '',
      name: meta.name ?? p.name,
      category: meta.category ?? p.category,
    };
  });
}

export function collectRoomObjectIdsFromStudioState(curatedItems, placedModels) {
  const ids = new Set();
  (curatedItems ?? []).forEach((item) => {
    if (item?.id != null) ids.add(String(item.id));
  });
  (placedModels ?? []).forEach((p) => {
    const id = p?.roomObjectId ?? p?.id;
    if (id != null) ids.add(String(id));
  });
  return [...ids].sort();
}
